import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { LegalDocument } from '@powerful/data';
import { Button } from '@powerful/ui';

export interface ReacceptancePromptProps {
  pending: LegalDocument[];
  loading?: boolean;
  error?: Error | null;
  acceptingDocumentId?: string | null;
  onAccept: (documentId: string) => Promise<void>;
  onComplete?: () => void;
  onRetry?: () => void;
  onLogout: () => void;
}

/**
 * Diálogo bloqueante de re-aceite. A seleção é local e deliberada; a liberação depende de
 * `onAccept`, que confirma cada revisão na função de servidor, e de `onComplete` após todas as
 * confirmações. Links e logout ficam sempre fora da condição do checkbox para não prender a pessoa.
 */
export function ReacceptancePrompt({
  pending,
  loading = false,
  error = null,
  acceptingDocumentId = null,
  onAccept,
  onComplete,
  onRetry,
  onLogout,
}: ReacceptancePromptProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const pendingIds = useMemo(() => new Set(pending.map((document) => document.id)), [pending]);
  const allSelected = pending.length > 0 && pending.every((document) => selected.has(document.id));

  useEffect(() => {
    setSelected((current) => {
      const next = new Set([...current].filter((id) => pendingIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [pendingIds]);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...dialog.querySelectorAll<HTMLElement>('[data-legal-dialog-focusable="true"]')]
        .filter((element) => !element.hasAttribute('disabled'));
      if (!focusable.length) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    dialog.addEventListener('keydown', onKeyDown);
    return () => dialog.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!loading && !error && pending.length === 0) return null;

  const toggle = (documentId: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(documentId)) next.delete(documentId);
      else next.add(documentId);
      return next;
    });
  };

  const submit = async () => {
    if (!allSelected || submitting || loading) return;
    setSubmitting(true);
    try {
      for (const document of pending) await onAccept(document.id);
      onComplete?.();
    } catch {
      // O hook já expõe a falha e mantém o guard fechado; o diálogo continua montado para retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 overflow-y-auto bg-[color:var(--bg)]/95 px-4 py-8 backdrop-blur-sm sm:px-6" role="presentation">
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={loading || submitting}
        className="mx-auto flex min-h-full w-full max-w-2xl flex-col justify-center gap-6 rounded-xl border border-[color:var(--hairline)] bg-surface-1 p-6 shadow-[var(--shadow-2)] sm:p-10"
      >
        <div className="space-y-3">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.18em] text-gold-text">{t('legal.page.eyebrow')}</p>
          <h1 ref={titleRef} id={titleId} tabIndex={-1} className="font-display text-[length:var(--d-3)] leading-tight text-ink-1 outline-none sm:text-[length:var(--d-2)]">
            {t('legal.reacceptance.title')}
          </h1>
          <p className="max-w-xl text-[length:var(--t-base)] text-ink-2">{t('legal.reacceptance.body')}</p>
        </div>

        {loading && pending.length === 0 ? (
          <div role="status" aria-live="polite" className="rounded-lg border border-[color:var(--hairline)] bg-surface-2 p-5 text-ink-2">
            {t('legal.reacceptance.loading')}
          </div>
        ) : null}

        {pending.length > 0 ? (
          <div className="space-y-3" aria-label={t('legal.reacceptance.title')}>
            {pending.map((document) => {
              const checkboxId = `${titleId}-${document.id}`;
              const descriptionId = `${checkboxId}-description`;
              return (
                <div key={document.id} className="rounded-lg border border-[color:var(--hairline)] bg-surface-2 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-1">
                      <h2 className="font-display text-[length:var(--d-5)] text-ink-1">{document.title}</h2>
                      <p className="text-sm text-ink-2">{t('legal.page.version', { version: document.version })}</p>
                      {document.summary_of_changes ? <p id={descriptionId} className="text-sm text-ink-2">{document.summary_of_changes}</p> : null}
                    </div>
                    <Link
                      data-legal-dialog-focusable="true"
                      className="inline-flex min-h-[44px] shrink-0 items-center text-sm text-gold-text underline underline-offset-4 hover:text-gold"
                      to={`/legal/${document.kind}`}
                    >
                      {t('legal.reacceptance.read')}
                    </Link>
                  </div>
                  <label htmlFor={checkboxId} className="mt-3 flex min-h-[44px] cursor-pointer items-center gap-3 rounded-md p-2 text-sm text-ink-1 hover:bg-surface-3">
                    <input
                      id={checkboxId}
                      data-legal-dialog-focusable="true"
                      type="checkbox"
                      checked={selected.has(document.id)}
                      onChange={() => toggle(document.id)}
                      aria-describedby={document.summary_of_changes ? descriptionId : undefined}
                      className="h-5 w-5 accent-[color:var(--gold)]"
                    />
                    <span>{t('legal.reacceptance.checkbox', { version: document.version })}</span>
                  </label>
                </div>
              );
            })}
          </div>
        ) : null}

        {error ? (
          <div role="alert" className="rounded-lg border border-danger/50 bg-danger/[0.08] p-4 text-sm text-danger">
            <p>{t('legal.reacceptance.error')}</p>
            {onRetry ? (
              <Button data-legal-dialog-focusable="true" variant="danger" size="sm" className="mt-3" onClick={onRetry}>
                {t('legal.reacceptance.retry')}
              </Button>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-col-reverse gap-3 border-t border-[color:var(--hairline)] pt-5 sm:flex-row sm:items-center sm:justify-between">
          <Button data-legal-dialog-focusable="true" variant="ghost" onClick={onLogout}>
            {t('legal.reacceptance.logout')}
          </Button>
          {pending.length > 0 ? (
            <Button
              data-legal-dialog-focusable="true"
              variant="metal"
              size="lg"
              disabled={!allSelected || submitting || loading || Boolean(acceptingDocumentId)}
              onClick={() => void submit()}
            >
              {submitting || acceptingDocumentId ? t('legal.reacceptance.processing') : t('legal.reacceptance.submit')}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
